import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Track, TrackType } from "@/types";
import { TrackCard } from "./TrackCard";

interface TrackListProps {
  tracks: Track[];
  isLoading?: boolean;
  onEdit?: (track: Track) => void;
  onDelete?: (track: Track) => void;
  onView?: (track: Track) => void;
  onLeaderboard?: (track: Track) => void;
  emptyMessage?: string;
}

export const TrackList: React.FC<TrackListProps> = ({
  tracks,
  isLoading = false,
  onEdit,
  onDelete,
  onView,
  onLeaderboard,
  emptyMessage = "",
}) => {
  const { t } = useTranslation(["pages", "common"]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<TrackType | "all">("all");
  const [sortBy, setSortBy] = useState<"name" | "type" | "createdAt">(
    "name",
  );

  const trackTypeOptions = [
    { value: "all", label: t("pages:tracks.list.allTypes") },
    { value: "circuit", label: t("pages:tracks.form.typeOptions.circuit") },
    { value: "gymkhana", label: t("pages:tracks.form.typeOptions.gymkhana") },
    { value: "drag_strip", label: t("pages:tracks.form.typeOptions.drag_strip") },
    { value: "hill_climb", label: t("pages:tracks.form.typeOptions.hill_climb") },
    { value: "rally", label: t("pages:tracks.form.typeOptions.rally") },
    { value: "oval", label: t("pages:tracks.form.typeOptions.oval") },
    { value: "karting", label: t("pages:tracks.form.typeOptions.karting") },
    { value: "other", label: t("pages:tracks.form.typeOptions.other") },
  ];

  const filteredAndSortedTracks = tracks
    .filter((track) => {
      // Search filter
      const matchesSearch =
        searchTerm === "" ||
        track.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        track.description?.toLowerCase().includes(searchTerm.toLowerCase());

      // Type filter
      const matchesType = filterType === "all" || track.type === filterType;

      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "type":
          return a.type.localeCompare(b.type);
        case "createdAt":
        default:
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
      }
    });

  if (isLoading) {
    return (
 <div className="flex flex-col gap-2">
        {[...Array(3)].map((_, index) => (
          <div
            key={index}
 className="bg-white rounded-lg border border-secondary-200 px-4 py-3 animate-pulse"
          >
 <div className="h-4 bg-secondary-200 rounded w-1/3 mb-2"></div>
 <div className="h-3 bg-secondary-200 rounded w-2/3"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
 <div className="space-y-6">
      {/* Filters and Search */}
 <div className="bg-white rounded-lg border border-secondary-200 p-4">
 <div className="flex flex-col md:flex-row gap-4">
 <div className="flex-1">
            <input
              type="text"
              placeholder={t("pages:tracks.list.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
 className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

 <div className="flex gap-3">
            <select
              value={filterType}
              onChange={(e) =>
                setFilterType(e.target.value as TrackType | "all")
              }
 className="px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {trackTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={sortBy}
              onChange={(e) =>
                setSortBy(e.target.value as "name" | "type" | "createdAt")
              }
 className="px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="createdAt">{t("pages:tracks.list.sortByDate")}</option>
              <option value="name">{t("pages:tracks.list.sortByName")}</option>
              <option value="type">{t("pages:tracks.list.sortByType")}</option>
            </select>
          </div>
        </div>

        {filteredAndSortedTracks.length !== tracks.length && (
          <div className="mt-3 text-sm text-secondary-600">
            {t("pages:tracks.list.showingCount", {
              shown: filteredAndSortedTracks.length,
              total: tracks.length,
            })}
          </div>
        )}
      </div>

      {/* Track List */}
      {filteredAndSortedTracks.length === 0 ? (
 <div className="bg-white rounded-lg border border-secondary-200 p-8 text-center">
 <div className="text-secondary-400 mb-4">
            <svg
 className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-secondary-900 mb-2">
            {t("pages:tracks.list.noTracksFound")}
          </h3>
          <p className="text-secondary-600">{emptyMessage || t("pages:tracks.list.defaultEmpty")}</p>
        </div>
      ) : (
 <div className="flex flex-col gap-2">
          {filteredAndSortedTracks.map((track) => (
            <TrackCard
              key={track.id}
              track={track}
              onEdit={onEdit}
              onDelete={onDelete}
              onView={onView}
              onLeaderboard={onLeaderboard}
            />
          ))}
        </div>
      )}
    </div>
  );
};
